import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { personApi, type PersonRequest } from '../api/people'
import { userDirectoryApi, type DirectoryUser } from '../api/userDirectory'
import {
  FormGrid,
  Button,
  ButtonLink,
  Callout,
  Card,
  CardContent,
  CardHeader,
  LoadingState,
  Page as PageShell,
  PageHeader,
  Select,
  TextField,
} from '../components/ui'

/**
 * TASK-0184: minimal EDIT-only page for a `Person`, reachable exclusively via
 * `/people/:id/edit` — the destination of someone else's duplicate-conflict resolution
 * (`CustomerFormPage.tsx`/`CompanyFormPage.tsx`'s "Usa questo record esistente" button when the
 * matched candidate is a `Person`), or a direct URL. Direct replica of `ContactFormPage.tsx`
 * (TASK-0171): deliberately no `mode` prop, no create/list routes, no duplicate-conflict UI of
 * its own — `PeopleController.Update` never runs a duplicate check, so this page can never hit
 * its own 409-from-duplicate the way Customer/Company create can.
 */

interface FormState {
  firstName: string
  lastName: string
  email: string
  phone: string
  fiscalCode: string
  address: string
  city: string
  province: string
  postalCode: string
  notes: string
  assignedAgentId: string
}

const EMPTY_FORM: FormState = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  fiscalCode: '',
  address: '',
  city: '',
  province: '',
  postalCode: '',
  notes: '',
  assignedAgentId: '',
}

export function PersonFormPage() {
  const { id: idParam } = useParams<{ id: string }>()
  const id = parseInt(idParam ?? '0', 10)

  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const [loadingData, setLoadingData] = useState(true)
  const [agents, setAgents] = useState<DirectoryUser[]>([])

  // Same justification as CompanyFormPage.tsx/ContactFormPage.tsx: AssignedAgentId names the
  // EqualAgente-role user handling this person, so the selector is filtered server-side to that
  // role.
  useEffect(() => {
    userDirectoryApi.list({ roles: ['EqualAgente'], activeOnly: false, limit: 200 })
      .then(setAgents)
      .catch(() => setAgents([]))
  }, [])

  useEffect(() => {
    setLoadingData(true)
    personApi
      .get(id)
      .then(person => {
        setForm({
          firstName: person.firstName ?? '',
          lastName: person.lastName ?? '',
          email: person.email ?? '',
          phone: person.phone ?? '',
          fiscalCode: person.fiscalCode ?? '',
          address: person.address ?? '',
          city: person.city ?? '',
          province: person.province ?? '',
          postalCode: person.postalCode ?? '',
          notes: person.notes ?? '',
          assignedAgentId: person.assignedAgentId != null ? String(person.assignedAgentId) : '',
        })
      })
      .catch(e => setError(e instanceof Error ? e.message : 'Errore nel caricamento'))
      .finally(() => setLoadingData(false))
  }, [id])

  function setField(
    key: keyof FormState,
  ): (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void {
    return e => setForm(f => ({ ...f, [key]: e.target.value }))
  }

  async function handleSave(): Promise<void> {
    setSaving(true)
    setError(null)
    setSaved(false)
    try {
      const body: PersonRequest = {
        firstName: form.firstName || 'Persona',
        lastName: form.lastName || 'Sconosciuto',
      }
      if (form.email) body.email = form.email
      if (form.phone) body.phone = form.phone
      if (form.fiscalCode) body.fiscalCode = form.fiscalCode
      if (form.address) body.address = form.address
      if (form.city) body.city = form.city
      if (form.province) body.province = form.province
      if (form.postalCode) body.postalCode = form.postalCode
      if (form.notes) body.notes = form.notes
      if (form.assignedAgentId) body.assignedAgentId = Number(form.assignedAgentId)

      await personApi.update(id, body)
      setSaved(true)
    } catch (err) {
      // Includes the 409 "già unito" case (PeopleController.Update, person.IsMerged) — the
      // shared api() client already turns it into a readable Error, shown here like any other
      // save failure. No special "already merged" UI (no canonical link, no redirect) — out of
      // scope per TASK-0184, same decision TASK-0171 made for Contact.
      setError(err instanceof Error ? err.message : 'Errore nel salvataggio')
    } finally {
      setSaving(false)
    }
  }

  if (loadingData) {
    return <LoadingState />
  }

  return (
    <PageShell>
      <PageHeader icon="person" back={{ to: '/customers', label: 'Clienti' }} title="Modifica persona" />

      {saved && !error && (
        <Callout tone="success">Salvato.</Callout>
      )}
      {error && <Callout tone="error">{error}</Callout>}

      <Card variant="outlined">
        <CardHeader title="Informazioni anagrafiche" />
        <CardContent>
          <FormGrid>
            <div>
              <TextField
                label="Nome *"
                id="prf-firstName"
                type="text"
                required
                value={form.firstName}
                onChange={setField('firstName')}
              />
            </div>
            <div>
              <TextField
                label="Cognome *"
                id="prf-lastName"
                type="text"
                required
                value={form.lastName}
                onChange={setField('lastName')}
              />
            </div>
          </FormGrid>

          <FormGrid columns={3} className="mt-3">
            <div>
              <TextField
                label="Email"
                id="prf-email"
                type="email"
                value={form.email}
                onChange={setField('email')}
              />
            </div>
            <div>
              <TextField
                label="Telefono"
                id="prf-phone"
                type="text"
                value={form.phone}
                onChange={setField('phone')}
              />
            </div>
            <div>
              <Select
                label="Agente assegnato"
                id="prf-assignedAgentId"
                value={form.assignedAgentId}
                onChange={e => setForm(f => ({ ...f, assignedAgentId: e.target.value }))}
              >
                <option value="">Nessun agente</option>
                {agents.map(a => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </Select>
            </div>
          </FormGrid>

          <FormGrid columns={3} className="mt-3">
            <div>
              <TextField
                label="Codice Fiscale"
                id="prf-fiscalCode"
                type="text"
                value={form.fiscalCode}
                onChange={setField('fiscalCode')}
              />
            </div>
            <div>
              <TextField
                label="Indirizzo"
                id="prf-address"
                type="text"
                value={form.address}
                onChange={setField('address')}
              />
            </div>
            <div>
              <TextField
                label="Città"
                id="prf-city"
                type="text"
                value={form.city}
                onChange={setField('city')}
              />
            </div>
          </FormGrid>

          <FormGrid columns={3} className="mt-3">
            <div>
              <TextField
                label="Provincia"
                id="prf-province"
                type="text"
                maxLength={2}
                value={form.province}
                onChange={e => setForm(f => ({ ...f, province: e.target.value.toUpperCase() }))}
              />
            </div>
            <div>
              <TextField
                label="CAP"
                id="prf-postalCode"
                type="text"
                value={form.postalCode}
                onChange={setField('postalCode')}
              />
            </div>
          </FormGrid>

          <FormGrid columns={1} className="mt-3">
            <div>
              <TextField
                label="Note"
                multiline
                id="prf-notes"
                rows={3}
                value={form.notes}
                onChange={setField('notes')}
              />
            </div>
          </FormGrid>
        </CardContent>
      </Card>

      <div className="d-flex gap-2">
        <Button
          variant="filled"
          icon="save"
          loading={saving}
          disabled={saving}
          onClick={() => void handleSave()}
        >
          {saving ? 'Salvataggio...' : 'Salva'}
        </Button>
        <ButtonLink to="/dashboard" variant="text">
          Chiudi
        </ButtonLink>
      </div>
    </PageShell>
  )
}
